import {Form, InputGroup} from "react-bootstrap";
import {useEffect, useState} from "react";
import UlpsPortalApiClient from "../UlpsPortalApiClient";
import Util from "../Util";

export function LicenceForm({licence, setLicence, setLoading, allFeatures = []}) {
  const [features, setFeatures] = useState([]);
  const [featureGroups, setFeatureGroups] = useState([]);
  const [availableFeatureGroups, setAvailableFeatureGroups] = useState([]);
  const [testMode, setTestMode] = useState(!!licence.testMode);
  const [licenceSchemas, setLicenceSchemas] = useState(null);
  const availableFeatures = licence.licenceSchema
    ? allFeatures.filter(feature => (feature.licenceSchemas || []).includes(licence.licenceSchema))
    : allFeatures;
 
  useEffect(() => {
    setLoading(true);
    UlpsPortalApiClient.getLicenceSchemas().then(result => {
      setLicenceSchemas(result);
      setLoading(false);
    });
    setFeatures(Array.isArray(licence.features) ? licence.features : []);
    setFeatureGroups(Array.isArray(licence.featureGroups) ? licence.featureGroups : []);
  }, [licence.features, licence.featureGroups]);

  useEffect(() => {
    if (!licence.licenceSchema) {
      setAvailableFeatureGroups([]);
      return;
    }

    UlpsPortalApiClient.getFeatureGroups(licence.licenceSchema)
      .then(result => {
        setAvailableFeatureGroups(Array.isArray(result) ? result : []);
      })
      .catch(err => {
        console.error("Unable to fetch feature groups.", err);
        setAvailableFeatureGroups([]);
      });
  }, [licence.licenceSchema]);

  const handleChange = prop => evt => {
    const value = evt.target.value;
    if (prop === "licenceSchema") {
      setFeatures([]);
      setFeatureGroups([]);
      setLicence({...licence, [prop]: value, features: [], featureGroups: []});
      return;
    }
    setLicence({...licence, [prop]: value});
  };

  const handleCheckboxChange = (prop, infiniteValue = null) => evt => {
    setLicence({...licence, [prop]: evt.target.checked ? infiniteValue : ""});
  };

  function getValidity(days) {
    try {
      const parsedDays = Number(days);
      if (isNaN(parsedDays)) return "";
      return Util.getValidity(parsedDays, licence.firstActivatedAt, true);
    } catch (e) {
      return "";
    }
  }

  return (
    <>
      <InputGroup className="mb-2">
        <InputGroup.Prepend>
          <InputGroup.Text>Licence schema</InputGroup.Text>
        </InputGroup.Prepend>
        <Form.Control
          as="select"
          value={licence.licenceSchema || ""} 
          onChange={handleChange("licenceSchema")}
          required
        >
          <option value="" disabled>
            -- Choose licence schema --
          </option>

          {licenceSchemas?.map((sch) => (
            <option key={sch.id} value={sch.name}>
              {sch.name}
            </option>
          ))}
        </Form.Control>
      </InputGroup>
      
      <InputGroup className="mb-2">
        <InputGroup.Prepend>
          <InputGroup.Text>Support availability</InputGroup.Text>
        </InputGroup.Prepend>
        <Form.Control
          type="number"
          placeholder="Support validity days"
          min={1}
          max={3654}
          value={licence.supportValidityDays}
          onChange={handleChange("supportValidityDays")}
          required
        />
        {licence.firstActivatedAt && licence.supportValidityDays && (
          <InputGroup.Append>
            <InputGroup.Text muted>
              {getValidity(licence.supportValidityDays, licence.firstActivatedAt)}
            </InputGroup.Text>
          </InputGroup.Append>
        )}
      </InputGroup>

      <InputGroup className="mb-2">
        <InputGroup.Prepend>
          <InputGroup.Text>Licence available until</InputGroup.Text>
        </InputGroup.Prepend>

        <Form.Control
          type="number"
          placeholder="Licence validity days"
          min={1}
          max={5 * 366}
          value={licence.licenceValidityDays ?? ""}
          onChange={handleChange("licenceValidityDays")}
          disabled={licence.licenceValidityDays === null}
          required={licence.licenceValidityDays !== null}
        />

        {licence.firstActivatedAt && licence.licenceValidityDays && (
          <InputGroup.Append>
            <InputGroup.Text muted>
              {getValidity(licence.licenceValidityDays, licence.firstActivatedAt)}
            </InputGroup.Text>
          </InputGroup.Append>
        )}

        <InputGroup.Append>
          <InputGroup.Checkbox
            type="checkbox"
            checked={licence.licenceValidityDays === null}
            onChange={handleCheckboxChange("licenceValidityDays")}
          />
          <InputGroup.Text>Infinite</InputGroup.Text>
        </InputGroup.Append>
      </InputGroup>

      <InputGroup className="mb-2">
        <InputGroup.Prepend>
          <InputGroup.Text>Maximum streams</InputGroup.Text>
        </InputGroup.Prepend>
        <Form.Control
          type="number"
          placeholder="Maximum number of streams"
          min={1}
          value={licence.maxStreams ?? ""}
          onChange={handleChange("maxStreams")}
          disabled={licence.maxStreams === null}
          required={licence.maxStreams !== null}
        />

        <InputGroup.Append>
          <InputGroup.Checkbox
            checked={licence.maxStreams === null}
            onChange={handleCheckboxChange("maxStreams")}
          />
          <InputGroup.Text>Infinite</InputGroup.Text>
        </InputGroup.Append>
      </InputGroup>

      <InputGroup className="mb-2">
        <InputGroup.Prepend>
          <InputGroup.Text>Maximum version</InputGroup.Text>
        </InputGroup.Prepend>
        <Form.Control
          type="text"
          placeholder="Maximum product version"
          onChange={handleChange("maxProductVersion")}
          value={
            licence.maxProductVersion === null ||
            licence.maxProductVersion === "infinite"
              ? ""
              : licence.maxProductVersion
          }
          disabled={licence.maxProductVersion === "infinite"}
          required={licence.maxProductVersion !== "infinite"}
        />
        <InputGroup.Append>
          <InputGroup.Checkbox
            checked={licence.maxProductVersion === "infinite"}
            onChange={handleCheckboxChange("maxProductVersion", "infinite")}
          />
          <InputGroup.Text>Infinite</InputGroup.Text>
        </InputGroup.Append>
      </InputGroup>

      {/* ---------- FEATURES SELECTOR ---------- */}
      <InputGroup className="mb-2">
        <InputGroup.Prepend>
          <InputGroup.Text>Features</InputGroup.Text>
        </InputGroup.Prepend>

        <Form.Control
          as="select"
          value=""
          disabled={
            availableFeatures.filter(
              f => !features.some(ff => ff.name === f.name)
            ).length === 0
          }
          onChange={e => {
            const name = e.target.value;
            if (!name) return;

            const selected = availableFeatures.find(f => f.name === name);
            if (!selected) return;

            setLicence(prev => ({
              ...prev,
              features: [...prev.features, selected],
            }));

            setFeatures(prev =>
              prev.some(f => f.name === selected.name)
                ? prev
                : [...prev, selected]
            );
          }}
        >
          {availableFeatures.length > 0 ? <option value="">Select a feature...</option> : <option value="">No features available</option>}
          {availableFeatures
            .filter(f => !features.some(ff => ff.name === f.name))
            .map((feat, idx) => (
              <option key={idx} value={feat.name}>
                {feat.description || feat.name}
              </option>
            ))}
        </Form.Control>
      </InputGroup>

      <InputGroup className="mb-2">
        <InputGroup.Prepend>
          <InputGroup.Text>Feature Groups</InputGroup.Text>
        </InputGroup.Prepend>

        <Form.Control
          as="select"
          value=""
          disabled={!licence.licenceSchema || availableFeatureGroups.filter(
            group => !featureGroups.some(selected => selected.id === group.id)
          ).length === 0}
          onChange={e => {
            const groupId = Number(e.target.value);
            if (!groupId) return;

            const selected = availableFeatureGroups.find(group => group.id === groupId);
            if (!selected) return;

            setLicence(prev => ({
              ...prev,
              featureGroups: [...(prev.featureGroups || []), selected],
            }));

            setFeatureGroups(prev =>
              prev.some(group => group.id === selected.id)
                ? prev
                : [...prev, selected]
            );
          }}
        >
          {!licence.licenceSchema ? (
            <option value="">Select a licence schema first...</option>
          ) : availableFeatureGroups.length > 0 ? (
            <option value="">Select a feature group...</option>
          ) : (
            <option value="">No feature groups available</option>
          )}
          {availableFeatureGroups
            .filter(group => !featureGroups.some(selected => selected.id === group.id))
            .map(group => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
        </Form.Control>
      </InputGroup>

      <InputGroup className="mb-2">
        <InputGroup.Prepend>
          <InputGroup.Text>Current Feature Groups</InputGroup.Text>
        </InputGroup.Prepend>
        <div
          className="form-control"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "6px",
            flexWrap: "wrap",
            minHeight: "50px",
            height: "auto",
            maxHeight: "50vh",
            background: "#fff",
            overflowY: "auto",
          }}
        >
          {featureGroups.length > 0 ? (
            featureGroups.map(group => (
              <span
                key={group.id}
                style={{
                  padding: "4px 10px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  marginRight: "6px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  backgroundColor: "#eef4ff",
                  color: "#000",
                }}
                onClick={() => {
                  setFeatureGroups(prev => prev.filter(item => item.id !== group.id));
                  setLicence(prev => ({
                    ...prev,
                    featureGroups: (prev.featureGroups || []).filter(item => item.id !== group.id),
                  }));
                }}
              >
                {group.name}
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "#dc3545",
                  }}
                >
                  ×
                </span>
              </span>
            ))
          ) : (
            <span style={{ color: "#6c757d" }}>
              No feature groups selected
            </span>
          )}
        </div>
      </InputGroup>

      {/* ---------- CURRENT FEATURES LIST ---------- */}
      <InputGroup className="mb-2">
        <InputGroup.Prepend>
          <InputGroup.Text>Current Features</InputGroup.Text>
        </InputGroup.Prepend>
          <div
            className="form-control"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "6px",
              flexWrap: "wrap",
              minHeight: "50px",
              height: "auto",
              maxHeight: "50vh",
              background: "#fff",
              overflowY: "auto",
            }}
          >
            {features.length > 0 ? (
              features.map(feat => (
                <span
                  key={feat.name}
                  style={{
                    padding: "4px 10px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    marginRight: "6px",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                    backgroundColor: "#f8f9fa",
                    color: "#000",
                  }}
                  onClick={() => {
                    setFeatures(prev => prev.filter(f => f.name !== feat.name));
                    setLicence(prev => ({
                      ...prev,
                      features: prev.features.filter(f => f.name !== feat.name),
                    }));
                  }}
                >
                  {feat.description || feat.name}
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "18px",
                      color: "#dc3545",
                    }}
                  >
                    ×
                  </span>
                </span>
              ))
            ) : (
              <span style={{ color: "#6c757d" }}>
                No features selected
              </span>
            )}
          </div>
      </InputGroup>
      <InputGroup className="mb-2">
        <InputGroup.Prepend>
          <InputGroup.Text>Additional Options</InputGroup.Text>
        </InputGroup.Prepend>
        <div
          className="form-control"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "#fff",
            borderLeft: "none",
            borderRight: "none",
            minHeight: "50px",
          }}
        >
          {/* Auto-enable toggle */}
          <span
            onClick={() => {
              if (testMode) return;

              setLicence(prev => ({
                ...prev,
                autoEnable: !prev.autoEnable,
                testMode: false,
              }));
            }}
            style={{
              padding: "4px 10px",
              cursor: testMode ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              marginRight: "6px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              backgroundColor: testMode
                ? "#e9ecef"
                : licence.autoEnable
                  ? "#d4edda"
                  : "#f8f9fa",
              color: testMode ? "#6c757d" : "#000",
              opacity: testMode ? 0.6 : 1,
              pointerEvents: testMode ? "none" : "auto",
              transition: "0.15s ease-in-out",
            }}
            title={testMode ? "Disabled while Test mode is enabled" : ""}
          >
          <span style={{ fontSize: "14px", fontWeight: "bold" }}>
            {!testMode && licence.autoEnable ? "✓" : "□"}
          </span>
            Auto-enable
          </span>

          {/* Test mode toggle */}
          <span
            onClick={() => {
              setLicence(prev => ({
                ...prev,
                testMode: !prev.testMode,
                autoEnable: false,
              }));
              setTestMode(prev => !prev);
            }}
            style={{
              padding: "4px 10px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              marginRight: "6px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              backgroundColor: licence.testMode ? "#d4edda" : "#f8f9fa",
              color: "#000",
              transition: "0.15s ease-in-out",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = "0 0 6px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <span style={{ fontSize: "14px", fontWeight: "bold" }}>
              {licence.testMode ? "✓" : "□"}
            </span>
            Test Mode
          </span>
        </div>
      </InputGroup>
    </>
  );
}
